/**
 * IssueMelDialog Component
 * Modal dialog to confirm issuing an MEL for an aircraft
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import type { MelItem } from '@/types/mel';
import { REPAIR_CATEGORIES } from '@/types/mel';
import { MelDispatchStatus } from './MelDispatchStatus';
import Button from '@/components/ui/Button';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

// Using a simplified Aircraft type to avoid import issues
interface AircraftOption {
  id: number;
  tailNumber: string;
  aircraftType: string;
}

interface IssueMelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (aircraftId: number, melItemId: number, description: string) => void;
  melItem: MelItem | null;
  aircraft?: AircraftOption[];
  selectedAircraftId?: number;
  isLoading?: boolean;
  canDispatch?: 'YES' | 'NO' | 'CONDITIONAL';
}

export const IssueMelDialog: React.FC<IssueMelDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  melItem,
  aircraft = [],
  selectedAircraftId,
  isLoading = false,
  canDispatch,
}) => {
  const [aircraftId, setAircraftId] = useState<number | ''>(selectedAircraftId || '');
  const [description, setDescription] = useState('');

  // Reset form when dialog opens with a new item
  useEffect(() => {
    if (isOpen && melItem) {
      setAircraftId(selectedAircraftId || '');
      setDescription('');
    }
  }, [isOpen, melItem, selectedAircraftId]);

  // Don't render if not open or no item
  if (!isOpen || !melItem) return null;

  const repairCat = REPAIR_CATEGORIES[melItem.repairCategory] || {
    label: melItem.repairCategory,
    days: null,
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aircraftId || !description.trim()) return;
    onConfirm(aircraftId, melItem.id, description.trim());
  };

  const isValid = aircraftId !== '' && description.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        className={cn(
          'relative w-full max-w-lg max-h-[90vh] overflow-auto',
          'bg-background rounded-xl shadow-2xl border border-border'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Issue MEL</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5 text-foreground/60" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Content */}
          <div className="p-4 space-y-4">
            {/* MEL Item Summary */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-primary">
                      {melItem.itemNumber}
                    </span>
                    <span className="text-xs text-foreground/50">
                      ATA {melItem.ataChapter}
                    </span>
                  </div>
                  <h3 className="mt-1 font-medium text-foreground">{melItem.title}</h3>
                </div>
                {canDispatch && <MelDispatchStatus status={canDispatch} size="sm" />}
              </div>

              {/* Time Limit */}
              <div className="flex items-center gap-2 mt-3 text-sm text-foreground/60">
                <ClockIcon className="w-4 h-4" />
                <span>
                  {repairCat.label}
                  {repairCat.days && ` - Expires in ${repairCat.days} calendar days`}
                </span>
              </div>
            </div>

            {/* Warning for CONDITIONAL */}
            {canDispatch === 'CONDITIONAL' && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  This MEL item has conditions that must be met before dispatch. Ensure all
                  conditions are verified and documented.
                </p>
              </div>
            )}

            {/* Aircraft Selection */}
            <div>
              <label
                htmlFor="aircraft-select"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Select Aircraft <span className="text-danger">*</span>
              </label>
              <select
                id="aircraft-select"
                value={aircraftId}
                onChange={(e) => setAircraftId(e.target.value ? Number(e.target.value) : '')}
                className={cn(
                  'w-full h-11 px-3 rounded-md border border-border',
                  'bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                )}
                disabled={isLoading}
              >
                <option value="">Select an aircraft...</option>
                {aircraft.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.tailNumber} - {a.aircraftType}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="mel-description"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Description of Issue <span className="text-danger">*</span>
              </label>
              <textarea
                id="mel-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the specific issue (e.g., Left landing light bulb burned out, position verified inop by crew)"
                rows={3}
                className={cn(
                  'w-full px-3 py-2 rounded-md border border-border',
                  'bg-background text-foreground',
                  'placeholder:text-foreground/40',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
                  'resize-none'
                )}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-foreground/50">
                Include details about the specific equipment affected and how the issue was
                verified
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!isValid || isLoading}
              loading={isLoading}
              icon={<PaperAirplaneIcon className="w-4 h-4" />}
            >
              Issue MEL
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssueMelDialog;
